import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Animated number that counts up from 0 to `value` when scrolled into view.
 * Inspired by 21st.dev's NumberTicker but rebuilt with Intersection Observer
 * + requestAnimationFrame to stay dependency-free.
 *
 * - Fires once per mount (no re-counting on re-enter).
 * - Respects `prefers-reduced-motion` — shows the final number directly.
 * - Format helpers: `prefix`, `suffix`, `separator` (1,234 vs 1 234 vs 1.234).
 *
 * @param {object} props
 * @param {number} props.value             Target number.
 * @param {number} [props.duration=1600]   ms.
 * @param {string} [props.prefix='']
 * @param {string} [props.suffix='']
 * @param {string} [props.separator=' ']   Thousands separator.
 * @param {string} [props.className]
 *
 * @example
 *   <NumberTicker value={12000} suffix="+" /> → 0 → 12 000+
 */
export default function NumberTicker({
  value,
  duration = 1600,
  prefix = '',
  suffix = '',
  separator = ' ',
  className = '',
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const [started, setStarted] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (started) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setStarted(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return undefined;
    if (reduce) {
      setDisplay(value);
      return undefined;
    }

    const start = performance.now();
    let frame = 0;
    const tick = (now) => {
      const elapsed = now - start;
      // Cubic-ease-out for a natural deceleration toward the target.
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [started, value, duration, reduce]);

  const formatted = String(display).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return (
    <span ref={ref} className={className} aria-label={`${prefix}${value}${suffix}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
